import {
  Box,
  Chip,
  Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { WpAppliedChange } from '@/types/api/seo';

type Props = {
  changes: WpAppliedChange[];
  compact?: boolean;
};

const changeTypeLabels: Record<string, { label: string; color: string }> = {
  title_tag: { label: 'Title', color: '#1a0dab' },
  meta_description: { label: 'Meta', color: '#0D9D58' },
  slug: { label: 'Slug', color: '#F4B400' },
  content: { label: 'Content', color: '#054AD9' },
};

const impactChip = (status: string, positionDelta?: number | null) => {
  switch (status) {
    case 'improved':
      return (
        <Chip
          icon={<ArrowUpwardIcon sx={{ fontSize: 12 }} />}
          label={positionDelta != null ? `+${positionDelta.toFixed(1)} positions` : 'Improved'}
          size="small"
          color="success"
          sx={{ fontSize: 10, height: 20 }}
        />
      );
    case 'declined':
      return (
        <Chip
          icon={<ArrowDownwardIcon sx={{ fontSize: 12 }} />}
          label={positionDelta != null ? `${positionDelta.toFixed(1)} positions` : 'Declined'}
          size="small"
          color="error"
          sx={{ fontSize: 10, height: 20 }}
        />
      );
    case 'no_change':
      return (
        <Chip
          icon={<RemoveIcon sx={{ fontSize: 12 }} />}
          label="No Change"
          size="small"
          sx={{ fontSize: 10, height: 20 }}
        />
      );
    case 'pending': {
      return (
        <Chip
          icon={<ScheduleIcon sx={{ fontSize: 12 }} />}
          label="Measuring..."
          size="small"
          color="warning"
          sx={{ fontSize: 10, height: 20 }}
        />
      );
    }
    default:
      return (
        <Chip label="No data" size="small" sx={{ fontSize: 10, height: 20 }} />
      );
  }
};

const formatDaysAgo = (dateStr: string): string => {
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
};

const ChangeImpactTimeline = function ({ changes, compact = false }: Props): JSX.Element {
  if (changes.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
        No WordPress changes have been applied yet. Use AI Suggestions to apply optimizations.
      </Typography>
    );
  }

  const sorted = [...changes].sort((a, b) => b.appliedAt.localeCompare(a.appliedAt));

  return (
    <Box>
      {sorted.map((change, i) => {
        const typeInfo = changeTypeLabels[change.changeType] ?? { label: change.changeType, color: '#96979b' };
        const positionDelta = change.positionBefore != null && change.positionAfter != null
          ? change.positionBefore - change.positionAfter
          : null;

        return (
          <Box
            key={change.id}
            sx={{
              display: 'flex',
              gap: 1.5,
              mb: 1,
              pb: 1,
              borderBottom: i < sorted.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider',
            }}
          >
            {/* Timeline dot + line */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5, width: 12, flexShrink: 0 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: typeInfo.color, flexShrink: 0 }} />
              {i < sorted.length - 1 && (
                <Box sx={{ width: 1, flex: 1, backgroundColor: 'divider', mt: 0.5 }} />
              )}
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Header row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label={typeInfo.label}
                  size="small"
                  sx={{ fontSize: 9, height: 18, color: typeInfo.color, borderColor: typeInfo.color }}
                  variant="outlined"
                />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  {new Date(change.appliedAt).toLocaleDateString()} ({formatDaysAgo(change.appliedAt)})
                </Typography>
                <Box sx={{ ml: 'auto' }}>
                  {impactChip(change.impactStatus, positionDelta)}
                </Box>
              </Box>

              {/* Before/After values (non-compact mode) */}
              {!compact && (
                <Box sx={{ mb: 0.75 }}>
                  {change.previousValue && (
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 0.25 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, fontWeight: 600, minWidth: 40 }}>Before:</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, textDecoration: 'line-through' }}>
                        {change.previousValue.length > 80 ? change.previousValue.slice(0, 80) + '...' : change.previousValue}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600, minWidth: 40, color: '#0D9D58' }}>After:</Typography>
                    <Typography variant="caption" sx={{ fontSize: 10, color: '#0D9D58' }}>
                      {change.newValue.length > 80 ? change.newValue.slice(0, 80) + '...' : change.newValue}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Metrics comparison */}
              {(change.positionBefore != null || change.positionAfter != null) && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {change.positionBefore != null && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>Position</Typography>
                      <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11, display: 'block' }}>
                        #{change.positionBefore.toFixed(1)}
                        {change.positionAfter != null && (
                          <Box component="span" sx={{ color: positionDelta != null && positionDelta > 0 ? '#0D9D58' : positionDelta != null && positionDelta < 0 ? '#EA4335' : 'text.secondary' }}>
                            {' → '}#{change.positionAfter.toFixed(1)}
                          </Box>
                        )}
                      </Typography>
                    </Box>
                  )}
                  {change.clicksBefore != null && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>Clicks</Typography>
                      <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11, display: 'block' }}>
                        {change.clicksBefore}
                        {change.clicksAfter != null && ` → ${change.clicksAfter}`}
                      </Typography>
                    </Box>
                  )}
                  {change.ctrBefore != null && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>CTR</Typography>
                      <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11, display: 'block' }}>
                        {(change.ctrBefore * 100).toFixed(1)}%
                        {change.ctrAfter != null && ` → ${(change.ctrAfter * 100).toFixed(1)}%`}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ChangeImpactTimeline;
